import { useRef, useCallback, useEffect } from "react";
import { TextureLoader, Color } from "three";
import { USDZExporter } from "three/examples/jsm/exporters/USDZExporter.js";
import { GLTFExporter } from "three-stdlib";
import { warn } from "../utils/logger";
import {
	setMeshMaterialRoughness,
	setMeshMaterialTexture,
	setMeshMaterialNormal,
	setMeshMaterialAo,
	setMeshMaterialAlpha,
	setMeshMaterialMetalness,
	setMeshMaterialBump,
} from "../utils/setMaterialTexture";
import {
	findMeshInGltf,
	getGltfMetadata,
	getGltfScenesInfo,
	printGltfSceneStructure,
} from "../utils/gltfInfo";

const DEFAULT_EXPORT_OPTIONS = {
	binary: true,
	// trs: true,
	onlyVisible: false,
	includeCustomExtensions: true,
};

export const useCreateRendererApi = (modelUrl, onApiReady) => {
	const readyState = useRef({ gltf: null, screenshot: null, three: null });
	const apiRef = useRef(null);

	useEffect(() => {
		readyState.current.gltf = null;
		apiRef.current = null;
	}, [modelUrl]);

	useEffect(() => {
		//here for the renderer init - so its promise is resolved on the second call even if same model is used
		if (onApiReady && apiRef.current) {
			onApiReady(apiRef.current);
		}
	}, [onApiReady]);

	const checkAndCreateApi = () => {
		const { gltf, screenshot } = readyState.current;
		let rendererApi;

		if (screenshot) {
			rendererApi = {
				getScreenshot: () => screenshot,
				//exposed for the render-runner
				getThree: () => readyState.current.three,
				printSceneStructure: () => printGltfSceneStructure(gltf),
			};

			if (gltf) {
				const getMesh = (name) => {
					const mesh = findMeshInGltf(gltf, name);
					if (!mesh) {
						warn(`[d8s renderer]: API: Failed to find mesh with name: ${name}`);
					}

					return mesh;
				};

				const getMaterial = (name) => {
					const material = gltf.materials[name];
					if (!material) {
						warn(`[d8s renderer]: API: Failed to find material with name: ${name}`);
					}

					return material;
				};

				rendererApi = {
					...rendererApi,
					replaceMeshMap: (name, textureUrl, onLoad) => {
						const mesh = getMesh(name);
						if (mesh) {
							setMeshMaterialTexture(mesh, textureUrl, onLoad, mesh);
						}
					},
					replaceMeshRoughnessMap: (name, roughnessUrl, onLoad) => {
						const mesh = getMesh(name);
						if (mesh) {
							setMeshMaterialRoughness(mesh, roughnessUrl, onLoad);
						}
					},
					replaceMeshNormalMap: (name, normalUrl, onLoad) => {
						const mesh = getMesh(name);
						if (mesh) {
							setMeshMaterialNormal(mesh, normalUrl, onLoad);
						}
					},
					replaceMeshAlphaMap: (name, alphaUrl, onLoad) => {
						const mesh = getMesh(name);
						if (mesh) {
							setMeshMaterialAlpha(mesh, alphaUrl, onLoad);
						}
					},
					replaceMeshAoMap: (name, aoUrl, onLoad) => {
						const mesh = getMesh(name);
						if (mesh) {
							setMeshMaterialAo(mesh, aoUrl, onLoad);
						}
					},
					replaceMeshMetalnessMap: (name, metalnessUrl, onLoad) => {
						const mesh = getMesh(name);
						if (mesh) {
							setMeshMaterialMetalness(mesh, metalnessUrl, onLoad);
						}
					},
					replaceMeshBumpMap: (name, bumpUrl, onLoad) => {
						const mesh = getMesh(name);
						if (mesh) {
							setMeshMaterialBump(mesh, bumpUrl, onLoad);
						}
					},
					getMaterial,
					setMaterialColor: (name, ...args) => {
						const material = getMaterial(name);

						if (material) {
							material.color = new Color(...args);
						}
					},
					getMesh,
					replaceMeshMaterial: (name, newMaterial) => {
						const mesh = gltf.scene.children.find((c) => c.name === name);
						if (!mesh) {
							warn(`[d8s renderer]: API: Failed to find mesh with name: ${name}`);
						}

						const prevName = mesh.material.name;
						newMaterial.name = prevName;
						mesh.material = newMaterial;
						gltf.materials[prevName] = newMaterial;
					},
					loadTexture: (url) =>
						new TextureLoader().loadAsync(url),

					//if successful, resolves an array buffer (by default since binary=true)
					exportGltf: (options = DEFAULT_EXPORT_OPTIONS) => {
						const exporter = new GLTFExporter();
						return new Promise((resolve, reject) => {
							exporter.parse(gltf.scene, resolve, reject, options);
						});
					},

					exportUsdz: () => {
						const exporter = new USDZExporter();
						// Clone and reset matrix on gltf so we get original values
						const clone = gltf.scene.clone(true);
						clone.position.set(0, 0, 0);
						clone.rotation.set(0, 0, 0);
						clone.scale.setScalar(1);
						clone.updateWorldMatrix(false, true);
						return exporter.parseAsync(clone);
					},

					getGltfInfo: () => {
						const currentGltf = readyState.current.gltf;
						return currentGltf && {
							scenes: getGltfScenesInfo(currentGltf),
							metadata: getGltfMetadata(currentGltf),
						};
					}
				};

			}
			window._d8sRendererApi = rendererApi;
			onApiReady(rendererApi);
			apiRef.current = rendererApi;
		}
	};

	const onModelReady = useCallback((loadedGltf) => {
		readyState.current.gltf = loadedGltf;
		checkAndCreateApi();
	}, []);

	const onRenderingReady = useCallback((gl, scene, camera, readyScreenshot) => {
		readyState.current.screenshot = readyScreenshot;
		readyState.current.three = { gl, scene, camera };
		checkAndCreateApi();
	}, []);

	return {
		onModelReady,
		onRenderingReady,
	};
};
